import React from "react";
import TextField from "@mui/material/TextField";
import EmployeeSchedulingPreferences, { schedulingFieldSx } from "../../../requests/manager/employees/EmployeeSchedulingPreferences";

export default function AutoScheduleEmployeeSettings({ members = [], canManage = false, onSave }) {
  const [search, setSearch] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const visibleMembers = members.filter((member) =>
    !member.is_archived && !member.is_hidden && member.status !== "archived" && member.status !== "hidden");
  const editingMember = visibleMembers.find((member) => member.id === editingId);
  const filtered = visibleMembers.filter((member) =>
    `${member.first_name ?? ""} ${member.last_name ?? ""}`.toLowerCase().includes(search.toLowerCase().trim()));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="shrink-0">
        <h3 className="text-sm font-semibold text-slate-900">Saved employee preferences</h3>
        <p className="mt-1 text-sm leading-5 text-slate-500">Reused in future runs, separate from optimization settings.</p>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <TextField fullWidth label="Find an employee" size="small" value={search} sx={schedulingFieldSx}
          onChange={(event) => setSearch(event.target.value)} />
        <span className="shrink-0 text-sm text-slate-500" aria-live="polite">{filtered.length} {filtered.length === 1 ? "employee" : "employees"}</span>
      </div>
      {!canManage && <p className="text-sm text-slate-500">Manager access at this location is required to edit employee preferences.</p>}
      <div role="region" aria-label="Employee preferences" tabIndex={0}
        className="min-h-0 max-h-[480px] overflow-auto rounded-lg border border-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">
        <table className="w-full min-w-[560px] border-separate border-spacing-0 text-left text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-slate-500">
            <tr>
              <th scope="col" className="border-b border-slate-200 px-4 py-3 font-medium">Employee</th>
              <th scope="col" className="whitespace-nowrap border-b border-slate-200 px-3 py-3 font-medium">Weekly target</th>
              <th scope="col" className="border-b border-slate-200 px-3 py-3 font-medium">Priority</th>
              <th scope="col" className="whitespace-nowrap border-b border-slate-200 px-3 py-3 font-medium">Min. shift</th>
              {canManage && <th scope="col" className="border-b border-slate-200 px-4 py-3"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((member) => (
              <tr key={member.id} className="group hover:bg-slate-50">
                <th scope="row" className="border-b border-slate-100 px-4 py-2.5 font-medium text-slate-900 group-last:border-0">{member.first_name} {member.last_name}</th>
                <td className="whitespace-nowrap border-b border-slate-100 px-3 py-2.5 tabular-nums text-slate-600 group-last:border-0">{Number(member.target_weekly_hours) > 0 ? `${member.target_weekly_hours} hrs` : "—"}</td>
                <td className="border-b border-slate-100 px-3 py-2.5 group-last:border-0">
                  {member.prioritize_target_hours && Number(member.target_weekly_hours) > 0
                    ? <span className="rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700">On</span>
                    : <span className="text-slate-500">Off</span>}
                </td>
                <td className="whitespace-nowrap border-b border-slate-100 px-3 py-2.5 tabular-nums text-slate-600 group-last:border-0">{member.preferred_min_paid_shift_hours ? `${member.preferred_min_paid_shift_hours} hrs` : "—"}</td>
                {canManage && <td className="border-b border-slate-100 px-4 py-2 text-right group-last:border-0">
                  <button type="button" aria-label={`Edit scheduling preferences for ${member.first_name} ${member.last_name}`}
                    onClick={() => setEditingId(member.id)}
                    className="rounded-md px-2 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">Edit</button>
                </td>}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={canManage ? 5 : 4} className="px-4 py-10 text-center text-slate-500">{visibleMembers.length ? "No employees match your search." : "No employees are available at this location."}</td></tr>}
          </tbody>
        </table>
      </div>
      <EmployeeSchedulingPreferences open={Boolean(editingMember) && canManage} member={editingMember} onClose={() => setEditingId(null)} onSave={onSave} />
    </div>
  );
}
